import { getIntl } from 'umi';

import { bindServices } from '@/services/api';
import HttpService from '@/services/http';

import { WhiteLabelCustomDomainStatus, MODEL_NAME, ROUTES } from './constants';
import {
  buildBrandingPayload,
  extractValidationErrors,
  getApiErrorMessage,
  getHttpStatus,
  isEmptyLocalResponse,
  mergeDomainResponse,
  normalizeWhiteLabelCustomDomainStatus,
  resolveFetchErrorCode,
  unwrapEntity,
} from './utils';

const suppressErrorNotification = { suppressErrorNotification: true };

const versionConflictNotification = () => ({
  visible: true,
  type: 'warning',
  message: getIntl().formatMessage({ id: 'whiteLabel.errors.versionConflict' }),
});

const api = bindServices(MODEL_NAME);
const namespace = MODEL_NAME;

function* applyWriteAllowed(put, select) {
  const writeAllowed = yield select((state) => state[namespace].writeAllowed);
  yield put({ type: 'setCanEdit', payload: writeAllowed });
}

/** Per-resource cache. UI spinners use `state.loading.effects[\`${namespace}/fetchLocal\`]`, etc. */
const createResourceState = () => ({
  error: null,
  loaded: false,
});

const initialState = {
  canEdit: false,
  /** Intended Write (owner session). Applied to `canEdit` only after Read succeeds. */
  writeAllowed: false,
  isCreate: true,
  local: {
    ...createResourceState(),
    data: null,
  },
  effective: {
    ...createResourceState(),
    data: null,
  },
  assetRequirements: {
    ...createResourceState(),
    data: null,
  },
  brandingSaving: false,
  brandingValidationErrors: [],
  domainBusy: false,
  /** Live form values for local preview (includes unsaved uploads). */
  draft: null,
  /** One-shot DNS token from register response (never returned again by API). */
  domainVerification: null,
  versionConflict: false,
};

const WhiteLabelModel = {
  namespace,
  state: initialState,
  effects: {
    *init({ payload }, { put }) {
      yield put({
        type: 'updateState',
        payload: { canEdit: false, writeAllowed: !!payload?.canEdit },
      });
      yield put({ type: 'fetchLocal', payload: { force: true } });
      yield put({ type: 'fetchEffective', payload: { force: true } });
      yield put({ type: 'fetchAssetRequirements', payload: { force: true } });
    },

    *resetState(_, { put }) {
      yield put({ type: 'reset' });
    },

    *fetchLocal({ payload }, { call, put, select }) {
      const { loaded } = yield select((state) => state[namespace].local);
      const inFlight = yield select(
        (state) => state.loading.effects[`${namespace}/fetchLocal`],
      );
      if (!payload?.force && (loaded || inFlight)) return;

      yield put({ type: 'setLocalState', payload: { error: null } });
      try {
        const result = yield call(
          api.getAll,
          {
            route: ROUTES.LOCAL,
            readPriority: true,
          },
          suppressErrorNotification,
        );

        // umi-request treats 204 as success with empty body (`''`)
        if (isEmptyLocalResponse(result)) {
          yield put({
            type: 'updateState',
            payload: {
              isCreate: true,
              domainVerification: null,
              versionConflict: false,
              draft: null,
            },
          });
          yield put({
            type: 'setLocalState',
            payload: { loaded: true, data: null, error: null },
          });
          yield* applyWriteAllowed(put, select);
          return;
        }

        const data = unwrapEntity(result);
        yield put({
          type: 'updateState',
          payload: {
            isCreate: false,
            versionConflict: false,
          },
        });
        yield put({
          type: 'setLocalState',
          payload: { loaded: true, data, error: null },
        });
        yield* applyWriteAllowed(put, select);
      } catch (error) {
        const status = getHttpStatus(error);
        if (status === 204) {
          yield put({
            type: 'updateState',
            payload: { isCreate: true, domainVerification: null, draft: null },
          });
          yield put({
            type: 'setLocalState',
            payload: { loaded: true, data: null, error: null },
          });
          yield* applyWriteAllowed(put, select);
          return;
        }
        const currentData = yield select((state) => state[namespace].local.data);
        if (status === 403) {
          yield put({ type: 'updateState', payload: { canEdit: false } });
        }
        yield put({
          type: 'setLocalState',
          payload: currentData
            ? { loaded: true, error: null }
            : {
                loaded: false,
                data: null,
                error: resolveFetchErrorCode(error),
              },
        });
      }
    },

    *fetchEffective({ payload }, { call, put, select }) {
      const { loaded } = yield select((state) => state[namespace].effective);
      const inFlight = yield select(
        (state) => state.loading.effects[`${namespace}/fetchEffective`],
      );
      if (!payload?.force && (loaded || inFlight)) return;

      yield put({ type: 'setEffectiveState', payload: { error: null } });
      try {
        const response = yield call(
          api.getAll,
          {
            route: ROUTES.EFFECTIVE,
            readPriority: true,
          },
          suppressErrorNotification,
        );

        // 204 = no applicable brand → AUTOMY defaults in the preview
        if (isEmptyLocalResponse(response)) {
          yield put({
            type: 'setEffectiveState',
            payload: { loaded: true, data: null, error: null },
          });
          return;
        }

        const data = unwrapEntity(response);
        yield put({
          type: 'setEffectiveState',
          payload: { loaded: true, data, error: null },
        });
      } catch (error) {
        const status = getHttpStatus(error);
        if (status === 204) {
          yield put({
            type: 'setEffectiveState',
            payload: { loaded: true, data: null, error: null },
          });
          return;
        }
        if (getHttpStatus(error) === 403) {
          yield put({ type: 'updateState', payload: { canEdit: false } });
        }
        yield put({
          type: 'setEffectiveState',
          payload: {
            loaded: false,
            error: resolveFetchErrorCode(error),
          },
        });
      }
    },

    *fetchAssetRequirements({ payload }, { call, put, select }) {
      const { loaded } = yield select((state) => state[namespace].assetRequirements);
      const inFlight = yield select(
        (state) => state.loading.effects[`${namespace}/fetchAssetRequirements`],
      );
      if (!payload?.force && (loaded || inFlight)) return;

      yield put({ type: 'setAssetRequirementsState', payload: { error: null } });
      try {
        const response = yield call(
          api.getAll,
          {
            route: ROUTES.ASSET_REQUIREMENTS,
            readPriority: true,
          },
          suppressErrorNotification,
        );
        const data = unwrapEntity(response);
        yield put({
          type: 'setAssetRequirementsState',
          payload: { loaded: true, data, error: null },
        });
      } catch (error) {
        if (getHttpStatus(error) === 403) {
          yield put({ type: 'updateState', payload: { canEdit: false } });
        }
        yield put({
          type: 'setAssetRequirementsState',
          payload: {
            loaded: false,
            error: resolveFetchErrorCode(error),
          },
        });
      }
    },

    *saveBranding({ payload }, { call, put, select }) {
      const canEdit = yield select((state) => state[namespace].canEdit);
      if (!canEdit) return;

      const isCreate = yield select((state) => state[namespace].isCreate);
      const local = yield select((state) => state[namespace].local.data);
      const version = local?.version;

      yield put({
        type: 'updateState',
        payload: { brandingSaving: true, versionConflict: false, brandingValidationErrors: [] },
      });
      try {
        const body = buildBrandingPayload(payload, { version, isCreate, original: local });
        const response = yield call(
          api.update,
          {
            route: ROUTES.LOCAL,
            data: body,
          },
          suppressErrorNotification,
        );
        const data = unwrapEntity(response) ?? { ...local, ...body };

        yield put({
          type: 'updateState',
          payload: {
            isCreate: false,
            brandingSaving: false,
            versionConflict: false,
            brandingValidationErrors: [],
          },
        });
        yield put({
          type: 'setLocalState',
          payload: { data, loaded: true, error: null },
        });
        yield put({ type: 'fetchLocal', payload: { force: true } });
        yield put({ type: 'fetchEffective', payload: { force: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        const status = getHttpStatus(error);
        yield put({ type: 'updateState', payload: { brandingSaving: false } });

        if (status === 409) {
          yield put({ type: 'updateState', payload: { versionConflict: true } });
          yield put({ type: 'fetchLocal', payload: { force: true } });
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: versionConflictNotification(),
          });
          return;
        }

        if (status === 403) {
          yield put({ type: 'updateState', payload: { canEdit: false } });
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: true,
              type: 'error',
              message:
                getApiErrorMessage(error) ||
                getIntl().formatMessage({ id: 'whiteLabel.errors.writeForbidden' }),
            },
          });
          return;
        }

        const validationErrors = extractValidationErrors(error);
        if (status === 400 && validationErrors.length) {
          yield put({
            type: 'updateState',
            payload: { brandingValidationErrors: validationErrors },
          });
          return;
        }

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'error',
            message: getApiErrorMessage(error),
          },
        });
      }
    },

    *registerDomain({ payload }, { call, put, select }) {
      const canEdit = yield select((state) => state[namespace].canEdit);
      if (!canEdit) return;

      const local = yield select((state) => state[namespace].local.data);
      yield put({ type: 'updateState', payload: { domainBusy: true, versionConflict: false } });
      try {
        const response = yield call(
          api.create,
          {
            route: ROUTES.CUSTOM_DOMAIN,
            data: {
              customDomain: payload.customDomain,
              version: local?.version,
            },
          },
          suppressErrorNotification,
        );
        const data = unwrapEntity(response);
        yield put({
          type: 'updateState',
          payload: {
            domainBusy: false,
            isCreate: false,
            domainVerification: {
              verificationToken: data?.verificationToken,
              txtRecordName: data?.txtRecordName,
              txtRecordValue: data?.txtRecordValue ?? data?.verificationToken,
              customDomain: data?.customDomain,
            },
          },
        });
        yield put({
          type: 'setLocalState',
          payload: {
            data: mergeDomainResponse(local, data),
            loaded: true,
            error: null,
          },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        yield put({ type: 'updateState', payload: { domainBusy: false } });
        yield* handleDomainError(error, put);
      }
    },

    *verifyDomain(_, { call, put, select }) {
      const canEdit = yield select((state) => state[namespace].canEdit);
      if (!canEdit) return;

      const local = yield select((state) => state[namespace].local.data);
      yield put({ type: 'updateState', payload: { domainBusy: true, versionConflict: false } });
      try {
        const response = yield call(
          api.create,
          {
            route: ROUTES.CUSTOM_DOMAIN_VERIFY,
            data: { version: local?.version },
          },
          suppressErrorNotification,
        );
        const data = unwrapEntity(response);
        yield put({ type: 'updateState', payload: { domainBusy: false } });
        yield put({
          type: 'setLocalState',
          payload: {
            data: mergeDomainResponse(local, data),
            loaded: true,
            error: null,
          },
        });

        const nextStatus = normalizeWhiteLabelCustomDomainStatus(
          data?.customDomainStatus ?? local?.customDomainStatus,
        );
        if (nextStatus === WhiteLabelCustomDomainStatus.Verified) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'success' },
          });
        } else if (nextStatus === WhiteLabelCustomDomainStatus.PendingVerification) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: true,
              type: 'info',
              message: getIntl().formatMessage({ id: 'whiteLabel.domain.verifyPending' }),
            },
          });
        }
      } catch (error) {
        yield put({ type: 'updateState', payload: { domainBusy: false } });
        yield* handleDomainError(error, put);
      }
    },

    *activateDomain(_, { call, put, select }) {
      const canEdit = yield select((state) => state[namespace].canEdit);
      if (!canEdit) return;

      const local = yield select((state) => state[namespace].local.data);
      yield put({ type: 'updateState', payload: { domainBusy: true, versionConflict: false } });
      try {
        const response = yield call(
          api.create,
          {
            route: ROUTES.CUSTOM_DOMAIN_ACTIVATE,
            data: { version: local?.version },
          },
          suppressErrorNotification,
        );
        const data = unwrapEntity(response);
        yield put({ type: 'updateState', payload: { domainBusy: false } });
        yield put({
          type: 'setLocalState',
          payload: {
            data: mergeDomainResponse(local, data),
            loaded: true,
            error: null,
          },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        yield put({ type: 'updateState', payload: { domainBusy: false } });
        yield* handleDomainError(error, put);
      }
    },

    *disableDomain(_, { call, put, select }) {
      const canEdit = yield select((state) => state[namespace].canEdit);
      if (!canEdit) return;

      const local = yield select((state) => state[namespace].local.data);
      yield put({ type: 'updateState', payload: { domainBusy: true, versionConflict: false } });
      try {
        const response = yield call(
          api.create,
          {
            route: ROUTES.CUSTOM_DOMAIN_DISABLE,
            data: { version: local?.version },
          },
          suppressErrorNotification,
        );
        const data = unwrapEntity(response);
        yield put({ type: 'updateState', payload: { domainBusy: false } });
        yield put({
          type: 'setLocalState',
          payload: {
            data: mergeDomainResponse(local, data),
            loaded: true,
            error: null,
          },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        yield put({ type: 'updateState', payload: { domainBusy: false } });
        yield* handleDomainError(error, put);
      }
    },

    *clearDomain(_, { call, put, select }) {
      const canEdit = yield select((state) => state[namespace].canEdit);
      if (!canEdit) return;

      const local = yield select((state) => state[namespace].local.data);
      yield put({ type: 'updateState', payload: { domainBusy: true, versionConflict: false } });
      try {
        const response = yield call(
          [HttpService.getInstance(), 'request'],
          `/${ROUTES.CUSTOM_DOMAIN}`,
          {
            method: 'delete',
            data: { version: local?.version },
            headers: { 'Content-Type': 'application/json' },
            namespace,
            suppressErrorNotification: true,
          },
        );
        const data = unwrapEntity(response);
        yield put({
          type: 'updateState',
          payload: { domainBusy: false, domainVerification: null },
        });
        yield put({
          type: 'setLocalState',
          payload: {
            data: mergeDomainResponse(local, {
              ...(data || {}),
              customDomain: null,
              customDomainStatus: WhiteLabelCustomDomainStatus.NotConfigured,
              verificationToken: null,
              txtRecordName: null,
              txtRecordValue: null,
            }),
            loaded: true,
            error: null,
          },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        yield put({ type: 'updateState', payload: { domainBusy: false } });
        yield* handleDomainError(error, put);
      }
    },
  },
  reducers: {
    updateState(state, { payload }) {
      return { ...state, ...payload };
    },
    setCanEdit(state, { payload }) {
      return { ...state, canEdit: !!payload };
    },
    setLocalState(state, { payload }) {
      return { ...state, local: { ...state.local, ...payload } };
    },
    setEffectiveState(state, { payload }) {
      return { ...state, effective: { ...state.effective, ...payload } };
    },
    setAssetRequirementsState(state, { payload }) {
      return { ...state, assetRequirements: { ...state.assetRequirements, ...payload } };
    },
    updateDraft(state, { payload }) {
      return { ...state, draft: payload ?? null };
    },
    reset() {
      return { ...initialState };
    },
  },
};

function* handleDomainError(error, put) {
  const status = getHttpStatus(error);
  if (status === 409) {
    yield put({ type: 'updateState', payload: { versionConflict: true } });
    yield put({ type: `${namespace}/fetchLocal`, payload: { force: true } });
    yield put({
      type: 'global/showNotificationsGlobal',
      payload: versionConflictNotification(),
    });
    return;
  }
  if (status === 403) {
    yield put({ type: 'updateState', payload: { canEdit: false } });
  }
  yield put({
    type: 'global/showNotificationsGlobal',
    payload: {
      visible: true,
      type: 'error',
      message:
        getApiErrorMessage(error) ||
        (status === 403
          ? getIntl().formatMessage({ id: 'whiteLabel.errors.writeForbidden' })
          : undefined),
    },
  });
}

export default WhiteLabelModel;
